import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    useBookingWizard,
    isOptionStepComplete,
    isScheduleStepComplete,
    FREE_TEXT_MAX_LENGTH
} from './BookingWizardContext';

// Step 2 — Seleziona data e orario. Due varianti secondo activityData.bookingType:
// - fixedSlots: elenco degli slot definiti dall'admin (posti residui se non nascosti;
//   sold-out e "non disponibile" disabilitati — isUnavailable ha precedenza sull'etichetta)
// - free: data + orario liberi, più il campo facoltativo "date e orari alternativi"

const formatSlotDate = (isoDate, language) => {
    const date = new Date(`${isoDate}T00:00:00`);
    if (Number.isNaN(date.getTime())) return isoDate;
    return date.toLocaleDateString(language, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
};

const StepSchedule = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { activityData, booking, updateBooking } = useBookingWizard();

    const isFixedSlots = activityData.bookingType === 'fixedSlots';
    const todayIso = new Date().toISOString().split('T')[0];
    const canProceed = isScheduleStepComplete(activityData, booking);

    // Guardia: senza opzione completa si torna allo step 1
    if (!isOptionStepComplete(activityData, booking)) {
        return <Navigate to="../opzione" replace />;
    }

    const selectSlot = (slot) => {
        if (slot.isSoldOut || slot.isUnavailable) return;
        updateBooking({
            slotId: slot._id,
            preferredDate: slot.date,
            preferredTime: slot.startTime
        });
    };

    return (
        <div className="w-full flex flex-col gap-6 items-center">
            <div className="text-center flex flex-col gap-2">
                <h2>{t('BookingWizard.schedule.title')}</h2>
                <p className="text-neutral-500">{isFixedSlots ? t('BookingWizard.schedule.fixedIntro') : t('BookingWizard.schedule.freeIntro')}</p>
            </div>

            {
                isFixedSlots &&
                <div className="w-full flex flex-col gap-3">
                    {
                        (activityData.slots || []).map((slot) => {
                            const isSelected = slot._id === booking.slotId;
                            const isBlocked = slot.isSoldOut || slot.isUnavailable;
                            const spotsKnown = !isBlocked && slot.remainingSpots != null;
                            return (
                                <button
                                    key={slot._id}
                                    disabled={isBlocked}
                                    className={`w-full border-2 p-4 rounded-xl text-start flex items-center justify-between gap-2
                                        ${isBlocked ? 'border-neutral-200 bg-neutral-100 cursor-not-allowed opacity-70' : isSelected ? 'border-[#225424] bg-green-50' : 'border-neutral-200 bg-white hover:bg-neutral-100'}`}
                                    onClick={() => selectSlot(slot)}
                                >
                                    <div className="flex flex-col gap-1">
                                        <div className="font-bold capitalize">{formatSlotDate(slot.date, i18n.language)}</div>
                                        <div className="text-neutral-600">{slot.startTime}{slot.endTime ? ` – ${slot.endTime}` : ''}</div>
                                        {spotsKnown && <div className="text-sm text-[#225424]">{t('BookingWizard.schedule.spotsAvailable', { count: slot.remainingSpots })}</div>}
                                        {slot.isUnavailable && <div className="text-sm text-neutral-500 font-bold">{t('BookingWizard.schedule.unavailable')}</div>}
                                        {!slot.isUnavailable && slot.isSoldOut && <div className="text-sm text-red-500 font-bold">{t('BookingWizard.schedule.soldOut')}</div>}
                                    </div>
                                    {!isBlocked && <i className="fi fi-rr-angle-right text-neutral-400"></i>}
                                </button>
                            );
                        })
                    }
                    {
                        (activityData.slots || []).length === 0 &&
                        <p className="text-center text-neutral-500">{t('BookingWizard.schedule.noSlots')}</p>
                    }
                </div>
            }

            {
                !isFixedSlots &&
                <div className="w-full flex flex-col gap-4">
                    <div className="flex flex-col gap-2 p-4 border-2 rounded-xl bg-white">
                        <label className="text-xs text-neutral-500" htmlFor="preferredDate">{t('BookingWizard.schedule.selectDate')}</label>
                        <input type="date" id="preferredDate" min={todayIso} value={booking.preferredDate || ''}
                            onChange={(event) => updateBooking({ preferredDate: event.target.value || null })} />
                        <label className="text-xs text-neutral-500 mt-2" htmlFor="preferredTime">{t('BookingWizard.schedule.startTime')}</label>
                        <input type="time" id="preferredTime" value={booking.preferredTime || ''}
                            onChange={(event) => updateBooking({ preferredTime: event.target.value || null })} />
                    </div>
                    <div className="flex flex-col gap-2">
                        <p className="text-sm text-neutral-500">{t('BookingWizard.schedule.alternativeNote')}</p>
                        <label className="text-xs text-neutral-500" htmlFor="alternativeSlots">{t('BookingWizard.schedule.alternativeLabel')}</label>
                        <textarea
                            id="alternativeSlots"
                            className="resize-none h-24"
                            maxLength={FREE_TEXT_MAX_LENGTH}
                            value={booking.alternativeSlots}
                            onChange={(event) => updateBooking({ alternativeSlots: event.target.value })}
                        ></textarea>
                        <div className="text-xs text-neutral-400 text-end">{t('BookingWizard.charsLeft', { count: FREE_TEXT_MAX_LENGTH - booking.alternativeSlots.length })}</div>
                    </div>
                </div>
            }

            {
                canProceed &&
                <button className="bg-[#225424] py-3 px-6 text-white rounded-xl w-full md:w-fit" onClick={() => navigate('../partecipanti')}>
                    {t('BookingWizard.proceed')}
                </button>
            }
        </div>
    );
};

export default StepSchedule;
